"use client";

import React, { useMemo } from "react";
import CountryFlag from "./CountryFlag";

const EMOJI_FONT =
  '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';

const FLAG_REGEX = /^[\u{1F1E0}-\u{1F1FF}]{2}$/u;
const REGION_EMOJI = new Set(["🌍", "🌎", "🌏", "🌐", "🇪🇺"]);

let _isWindows: boolean | null = null;
function isWindows(): boolean {
  if (_isWindows !== null) return _isWindows;
  if (typeof navigator === "undefined") return false;
  _isWindows = /Win/i.test(navigator.platform || "") || /Windows/i.test(navigator.userAgent || "");
  return _isWindows;
}

function flagEmojiToCode(emoji: string): string | null {
  if (!FLAG_REGEX.test(emoji)) return null;
  const points = [...emoji];
  if (points.length !== 2) return null;
  const A = 0x1f1e6;
  const c1 = String.fromCharCode((points[0].codePointAt(0)! - A) + 65);
  const c2 = String.fromCharCode((points[1].codePointAt(0)! - A) + 65);
  return c1 + c2;
}

const DEFAULT_EMOJI = "📊";

interface EtfEmojiProps {
  emoji?: string | null;
  size?: number;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Renders ETF emoji with cross-platform flag support:
 * - Country flags: native emoji on Apple/Linux, SVG on Windows
 * - Other emoji: native rendering on all platforms
 * - Fallback: 📊 if no emoji assigned
 */
export default function EtfEmoji({ emoji, size = 12, style, className }: EtfEmojiProps) {
  const displayEmoji = emoji || DEFAULT_EMOJI;
  const winFlag = useMemo(() => isWindows(), []);

  const countryCode = useMemo(() => flagEmojiToCode(displayEmoji), [displayEmoji]);

  if (countryCode && winFlag) {
    return <CountryFlag country={countryCode} size={size} style={style} className={className} />;
  }

  if (REGION_EMOJI.has(displayEmoji) || countryCode) {
    return (
      <span
        className={className}
        style={{
          fontSize: size,
          lineHeight: 1,
          fontFamily: EMOJI_FONT,
          flexShrink: 0,
          ...style,
        }}
      >
        {displayEmoji}
      </span>
    );
  }

  return (
    <span
      className={className}
      style={{
        fontSize: size,
        lineHeight: 1,
        fontFamily: EMOJI_FONT,
        flexShrink: 0,
        ...style,
      }}
    >
      {displayEmoji}
    </span>
  );
}
